import { GlobalRequest } from '~/components/global-requset'
import { GetServerSideProps } from 'next'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { GET_POST, VIEW_POST } from '~/graphqls/gqls'
import {
  ActionType,
  AffectedType,
  getPost,
  getPostVariables,
  PostTypes,
  Post_AiArtworkInfo,
  Post_Artwork,
  SocialViewType,
  SquarePostScope,
  viewPost,
  viewPostVariables,
} from '~/graphqls/types'
import { useImperativeQuery } from '~/hooks/useImperativeQuery'
import { MyDivide } from '~/components/ui/MyDivide'
import { serverSideCatch, toHtml, toYMDHm } from '~/utils'
import { Layout } from '~/layouts/default'
import { MyContainer } from '~/components/ui/MyContainer'
import ImageViewer from '~/components/pages/plaza/imageViewer/index'
import AiItemInfo, { IAiItemInfoRef } from '~/components/pages/plaza/AiItemInfo'
import AiUserInfo from '~/components/pages/plaza/AiUserInfo'
import { ReplyBar } from '~/components/pages/plaza/ReplyBar'
import { MyIcon } from '~/components/ui/MyIcon'
import classNames from 'classnames'
import { useDidUpdateEffect } from '~/hooks/useDidUpdateEffect'

import styles from './[id].module.css'
import { SEOHead } from '~/components/business/SEOHead'
import { MyBtn } from '~/components/ui/MyBtn'
import { CDNImage } from '~/components/ui/CDNImage'
import { useMutation } from '@apollo/client'
import { Complain } from '~/components/pages/plaza/Complain'
import AiItemTool from '~/components/pages/plaza/AiItemTool'
import DisLikeBtn from '~/components/pages/plaza/DisLikeBtn'
import CollectionArtWorksNav from '~/components/pages/collection/CollectionArtWorksNav'
import { useStores } from '~/hooks/useStores'
import { observer } from 'mobx-react-lite'
import { Recommend } from '~/components/pages/plaza/Recommend'

const TEXT_LIMIT = 104

const Detail = ({ post: _post }: { post: PostTypes }) => {
  const router = useRouter()
  const { store } = useStores()
  const requestLock = useRef(false)
  // 用于更新ai模型信息
  const [post, setPost] = useState(_post)
  const [contentVisible, setContentVisible] = useState(false)
  const getPostQuery = useImperativeQuery<getPost, getPostVariables>(GET_POST)
  const [viewPostAction] = useMutation<viewPost, viewPostVariables>(VIEW_POST)

  const isSelf = post?.user?.uid === store.userinfo?.base?.uid
  const isEdited = post?.updateTime && post?.updateTime !== post?.publishTime
  const [followStatus, setFollowStatus] = useState(
    post?.user?.social?.followStatus
  )
  /** 踩一踩动效 */
  const [disLikeVisibleApng, setDisLikeVisibleApng] = useState(false)

  const aiItemInfoRef = useRef<IAiItemInfoRef>(null)

  /** 指定查看帖子中单一作品（从其他合集作品过来的，产品特殊需求） */
  const imageUrl = router.query.imageUrl
  const isFromCollection = !!router.query.collectionId

  const id = Number(router.query.id)
  const param = router.query?.param as string
  const indexurl = router.query?.indexurl as string
  const offset = Number(router.query.offset) || 0

  /** 关注状态改变，请求接口更新ai模型信息 */
  useDidUpdateEffect(() => {
    requestLock.current = true
    let params: Record<string, any> = {}
    try {
      const parsedParam = JSON.parse(param)
      params = {
        offset: offset || 0,
        turnPageParam: parsedParam,
      }
    } catch (error) {}
    getPostQuery({
      id,
      ...params,
    })
      .then(res => {
        setPost(res.data.squareModule?.squarePost! as PostTypes)
      })
      .finally(() => {
        requestLock.current = false
      })
  }, [followStatus, id])

  // 浏览统计
  useEffect(() => {
    try {
      viewPostAction({
        variables: {
          input: {
            actionType: ActionType.CONFIRM,
            affectedType:
              post.postScope === SquarePostScope.INSIDE
                ? AffectedType.AI_ARTWORK
                : AffectedType.EXTERNAL_AI_ARTWORK,
            id: id,
            viewType: SocialViewType.SQUARE,
          },
        },
      })
    } catch (err) {}
  }, [])

  const onPrev = () => {
    if (requestLock.current) return
    router.replace(
      {
        pathname: `/plaza/${post.turnPageInfo?.prev}`,
        query: param
          ? {
              param,
              indexurl,
              offset: offset - 1,
            }
          : {},
      },
      undefined,
      {
        shallow: true,
      }
    )
  }
  const onNext = () => {
    if (requestLock.current) return
    router.replace(
      {
        pathname: `/plaza/${post.turnPageInfo?.next}`,
        query: param
          ? {
              param,
              indexurl,
              offset: offset + 1,
            }
          : {},
      },
      undefined,
      {
        shallow: true,
      }
    )
  }

  const onClose = () => {
    const indexurl = (router.query?.indexurl || '') as string
    if (indexurl) {
      router.replace(window.decodeURIComponent(indexurl))
    } else {
      router.back()
    }
  }

  return (
    <Layout headerType="no-margin">
      <SEOHead title={post?.title} />
      <MyContainer>
        <div
          className="flex justify-between items-start h-[809px] relative"
          style={{
            maxHeight: `calc(100vh - 56px)`,
          }}
        >
          <div
            className="flex-1 flex-shrink-0 h-full p-8 relative overflow-hidden"
            style={{
              background: `linear-gradient(90deg, #141416 0%, #1E1F21 100%)`,
            }}
          >
            <div className="absolute right-6 top-[34px] z-10">
              <MyBtn $dark className="!px-2" onClick={onClose}>
                <div className="flex items-center">
                  <MyIcon icon="close" className="text-[20px] text-inherit" />
                </div>
              </MyBtn>
            </div>
            <AiItemTool
              post={post}
              className="absolute right-[82px] top-[34px] z-10"
            />
            {disLikeVisibleApng && (
              <CDNImage
                src="/images/plaza/dislike-apng.png"
                className="w-[120px] h-[120px] absolute top-[40%] left-[50%] -translate-x-[50%] -translate-y-[50%] z-[9999]"
                alt="like"
              />
            )}

            {post?.turnPageInfo?.prev && (
              <div
                className={classNames(styles.switchArrow, 'prevButton')}
                onClick={onPrev}
              >
                <MyIcon
                  icon="arrow-right-simple"
                  className="text-[40px] font-normal text-inherit"
                />
              </div>
            )}

            <ImageViewer
              style={
                isFromCollection ? { height: `calc(100% - 80px)` } : undefined
              }
              key={post.id}
              previewImages={
                post?.imageList
                  ?.filter(
                    it => it.imageUrl && (!imageUrl || it.imageUrl === imageUrl)
                  )
                  ?.map(it => {
                    return {
                      url: it.imageUrl!,
                      width: post.coverImage?.width!,
                      height: post.coverImage?.height!,
                    }
                  }) || []
              }
              lazy={!isFromCollection}
            />
            {post?.turnPageInfo?.next && (
              <div
                className={classNames(styles.switchArrow, 'nextButton')}
                onClick={onNext}
              >
                <MyIcon
                  icon="arrow-right-simple"
                  className="text-[40px] font-normal text-inherit"
                />
              </div>
            )}
            {isFromCollection && <CollectionArtWorksNav />}
          </div>
          <div className="w-[336px] h-full relative ml-8">
            <div
              className="scrollbar-hidden overflow-y-auto"
              style={{
                maxHeight: 'calc(100% - 87px)',
              }}
            >
              <AiUserInfo
                post={post}
                setPost={setPost}
                followStatus={followStatus}
                onFollowChange={setFollowStatus}
              />
              <div className="text-base text-neutral-800 font-semibold mb-4 break-words">
                {post?.title}
              </div>
              <div className="text-neutral-500 text-xs mb-4 break-words">
                {(post?.content || '').length > TEXT_LIMIT &&
                !contentVisible ? (
                  <>
                    {(post?.content || '').substring(0, TEXT_LIMIT)}...
                    <span
                      className="font-semibold text-primary ml-2 cursor-pointer"
                      onClick={() => setContentVisible(true)}
                    >
                      查看更多
                    </span>
                  </>
                ) : (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: toHtml(post?.content || ''),
                    }}
                  ></div>
                )}
              </div>
              <AiItemInfo
                data={post?.aiArtworkInfo as Post_AiArtworkInfo}
                competitionData={post?.artwork as Post_Artwork}
                viewPermission={post?.visibleType}
                hasCopy
                hasTranslate
                hasAiParamCopy={post.postScope === SquarePostScope.INSIDE}
                isSelf={isSelf}
                post={post}
                followStatus={followStatus}
                onFollowChange={setFollowStatus}
                ref={aiItemInfoRef}
              />
              <div className="flex justify-between mt-4 text-neutral-400 text-xs">
                <div className="flex">
                  {isEdited
                    ? `编辑于 ${toYMDHm(post?.updateTime)}`
                    : toYMDHm(post?.publishTime)}
                  <Recommend
                    postId={post.id as number}
                    recommend={post?.recommend}
                    className="ml-3"
                  />
                </div>
                <div className="flex">
                  <DisLikeBtn
                    className="mr-4"
                    post={post}
                    animating={disLikeVisibleApng}
                    onChange={val => {
                      if (val) {
                        setDisLikeVisibleApng(true)
                        setTimeout(() => {
                          setDisLikeVisibleApng(false)
                        }, 1400)
                      }
                    }}
                  />
                  <Complain postId={post.id!} />
                </div>
              </div>

              <MyDivide space={16} dark />
              {/* 评论占位 */}
              <div className="w-full h-[40px]"></div>
            </div>
            <ReplyBar
              key={post?.id}
              post={post}
              showInfo={() => {
                aiItemInfoRef.current?.setModalOpen?.('才能一键同款哦~')
              }}
            />
          </div>
        </div>
      </MyContainer>
    </Layout>
  )
}

export const getServerSideProps: GetServerSideProps = async context => {
  return await serverSideCatch(async () => {
    const { initialState, client } = await GlobalRequest(context)
    let params: Record<string, any> = {}
    try {
      const param = JSON.parse(context.query?.param as string)
      const offset = Number(context.query?.offset)
      params = {
        offset: offset || 0,
        turnPageParam: param,
      }
    } catch (error) {}

    const id = Number(context.params?.id)
    const res = await client.query<getPost, getPostVariables>({
      query: GET_POST,
      variables: {
        id,
        ...params,
      },
      fetchPolicy: 'no-cache',
    })
    return {
      props: {
        initialState,
        post: res.data.squareModule?.squarePost!,
      },
    }
  })
}

export default observer(Detail)
